function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Empty } from "../../components/analytics-kit";
import account from "../../data/account";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import BackToApps from "../../components/back-to-apps";
import { Button } from "@progress/kendo-react-buttons";
import { kwService, fmtDate, Chip, RowMenu, Verdict, SelfSiteCard, selfRow } from "../keywords/common";

/*
 * AI Visibility (KI spec P3; layout per mockups/apps-redesign-2026-10-09.html
 * board 6). Whether AI assistants mention the brand when buyers ask
 * questions.
 *
 * Reads GET /keywords/ai/visibility?days=90 (rows: promptId, provider,
 * model, mentioned, cited, mentionContext, sources, competitorMentions,
 * answerExcerpt, checkedDate) joined to GET /ai/prompts, plus the
 * citation breakdown and the competitor registry (the "self"
 * row names the brand, used to spot questions that ask for it by name).
 * An answer = the LATEST row per (prompt, provider). Check now = POST
 * /ai/refresh (synchronous server side; see checkNow). Ids are raw 64-bit,
 * kept intact by the service's JSONbig parse; always String() them for joins.
 */

const PROVIDER_NAMES = {
  chat_gpt: "ChatGPT",
  chatgpt: "ChatGPT",
  openai: "ChatGPT",
  claude: "Claude",
  gemini: "Gemini",
  perplexity: "Perplexity",
  grok: "Grok"
};
const DEFAULT_PROVIDERS = ["chat_gpt", "claude", "gemini", "perplexity"];
function providerLabel(p) {
  return PROVIDER_NAMES[String(p || "").toLowerCase()] || String(p || "");
}
function parseList(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try {
    const x = JSON.parse(v);
    return Array.isArray(x) ? x : [];
  } catch (e) {
    return [String(v)];
  }
}
const isMention = c => !!(c && (c.mentioned || c.cited));
const squash = s => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
function joinNames(list) {
  if (list.length <= 1) return list.join("");
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}
class AiVisibilityViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "reload", () => {
      this.setState({
        prompts: null,
        vis: null,
        error: false
      }, () => this.load());
    });
    _defineProperty(this, "addPrompts", async () => {
      const lines = this.state.addText.split(/\n+/).map(t => t.trim()).filter(Boolean);
      if (!lines.length || this.state.saving) return;
      this.setState({
        saving: true
      });
      try {
        await kwService("/ai/prompts").postAsync(lines.map(prompt => ({
          prompt,
          topicGroup: null
        })));
        this.setState({
          saving: false,
          adding: false,
          addText: ""
        });
        this.reload();
      } catch (e) {
        this.setState({
          saving: false,
          notice: "Adding questions didn't work. Try again."
        });
      }
    });
    _defineProperty(this, "deletePrompt", async p => {
      if (this.state.busyId) return;
      this.setState({
        busyId: String(p.id)
      });
      try {
        await kwService("/ai/prompts").deleteAsync(p.id);
        this.setState({
          busyId: null,
          prompts: this.state.prompts.filter(x => String(x.id) !== String(p.id))
        });
      } catch (e) {
        this.setState({
          busyId: null,
          notice: "Delete didn't work. Try again."
        });
      }
    });
    // Check now: the refresh endpoint is SYNCHRONOUS server side and runs past
    // the API Gateway 29s cap at even 3 prompts x 4 models (504, while the
    // Lambda keeps writing snapshots). So: fire it (body = provider ARRAY,
    // [] = all), ignore the response, and poll until fresh snapshots land.
    _defineProperty(this, "checkNow", async () => {
      if (this.state.checking) return;
      const prevMax = (this.state.vis || []).map(r => r.checkedDate).sort().pop() || "";
      this.setState({
        checking: true,
        notice: "Asking each assistant your questions. Answers show up here as they come in, usually within a minute."
      });
      kwService("/ai/refresh").postAsync([]).catch(() => {/* expected 504; the worker continues */});
      let tries = 0;
      this._checkPoll = setInterval(async () => {
        tries += 1;
        try {
          const vis = await kwService("/ai/visibility?days=90").getAsync();
          const ids = new Set((this.state.prompts || []).map(p => String(p.id)));
          const scoped = (vis || []).filter(r => ids.has(String(r.promptId)));
          const newMax = scoped.map(r => r.checkedDate).sort().pop() || "";
          if (newMax > prevMax) {
            this.setState({
              vis: scoped,
              notice: "New answers are in."
            });
            if (tries >= 4) {
              clearInterval(this._checkPoll);
              this.setState({
                checking: false
              });
            }
          }
        } catch (e) {/* keep polling */}
        if (tries >= 12) {
          clearInterval(this._checkPoll);
          if (this._alive) this.setState({
            checking: false
          });
        }
      }, 8000);
    });
    // Answer audit: after a site removes something stale (a departed staff
    // member, a closed location), verify the AI answers stopped repeating it.
    // Matches are the warning; zero matches across recent checks is the all-clear.
    _defineProperty(this, "runAudit", () => {
      const term = this.state.auditTerm.trim().toLowerCase();
      if (!term) return;
      const prompts = this.state.prompts || [];
      const hits = (this.state.vis || []).filter(r => {
        const sources = parseList(r.sources).map(s => s && typeof s === "object" ? `${s.title || ""} ${s.url || ""}` : s);
        const comps = parseList(r.competitorMentions);
        const hay = [r.answerExcerpt, r.mentionContext, ...sources, ...comps].filter(Boolean).join(" \n ").toLowerCase();
        return hay.includes(term);
      }).map(r => ({
        ...r,
        promptText: (prompts.find(p => String(p.id) === String(r.promptId)) || {}).prompt || "(question removed)"
      })).sort((a, b) => String(b.checkedDate).localeCompare(String(a.checkedDate)));
      this.setState({
        auditRan: {
          term: this.state.auditTerm.trim(),
          hits
        }
      });
    });
    this.state = {
      prompts: null,
      vis: null,
      error: false,
      citations: null,
      competitors: [],
      expanded: null,
      // "promptId|provider"
      adding: false,
      addText: "",
      saving: false,
      auditTerm: "",
      auditRan: null,
      // Answer audit: scan stored answers for a term
      checking: false,
      notice: null,
      busyId: null
    };
    this._alive = true;
  }
  async componentDidMount() {
    if (Auth.isAuthenticated() || (await Auth.attachOrSignIn())) this.load();
  }
  componentWillUnmount() {
    this._alive = false;
    clearInterval(this._checkPoll);
  }
  async load() {
    try {
      const [prompts, vis, citations, competitors] = await Promise.all([kwService("/ai/prompts").getAsync(), kwService("/ai/visibility?days=90").getAsync(), kwService("/ai/visibility/citations").getAsync().catch(() => null), kwService("/competitors").getAsync().catch(() => null)]);
      if (!this._alive) return;
      const active = (prompts || []).filter(p => p.active !== false);
      // Scope snapshots to ACTIVE prompts: deleted prompts keep their
      // history server side, and counting them would skew the totals.
      const ids = new Set(active.map(p => String(p.id)));
      this.setState({
        prompts: active,
        vis: (vis || []).filter(r => ids.has(String(r.promptId))),
        citations,
        competitors
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  // Latest snapshot per (prompt, provider): one "answer".
  latestCells() {
    const cells = new Map();
    (this.state.vis || []).forEach(r => {
      const key = `${String(r.promptId)}|${String(r.provider).toLowerCase()}`;
      const prev = cells.get(key);
      if (!prev || new Date(r.checkedDate) > new Date(prev.checkedDate)) cells.set(key, r);
    });
    return cells;
  }
  providers() {
    const seen = [];
    (this.state.vis || []).forEach(r => {
      const p = String(r.provider).toLowerCase();
      if (!seen.includes(p)) seen.push(p);
    });
    const order = p => {
      const i = DEFAULT_PROVIDERS.indexOf(p === "chatgpt" || p === "openai" ? "chat_gpt" : p);
      return i < 0 ? 99 : i;
    };
    const list = seen.length ? seen : DEFAULT_PROVIDERS;
    return list.slice().sort((a, b) => order(a) - order(b));
  }

  // Brand names: the "self" competitor row (label + domain) and the account's company.
  brandTerms() {
    const self = (this.state.competitors || []).find(c => c.isSelf);
    const company = (account.get() || {}).company;
    const terms = [];
    if (self && self.label) terms.push(self.label);
    if (self && self.domain) terms.push(String(self.domain).replace(/^https?:\/\//, "").replace(/^www\./, "").split(".")[0]);
    if (company) terms.push(company);
    return terms.map(squash).filter(t => t.length >= 3);
  }
  asksByName(prompt, brands) {
    const q = squash(prompt && prompt.prompt);
    return brands.some(b => q.includes(b));
  }
  // ------------------------------------------------------------ renderers

  renderVerdict(prompts, latest, brands, cells) {
    if (!prompts.length) {
      return /*#__PURE__*/React.createElement(Verdict, {
        tone: "grey",
        title: "You're not asking AI assistants anything yet"
      }, "Add the questions your buyers ask, and we'll check whether ChatGPT, Claude, Gemini and Perplexity mention you.");
    }
    if (!latest.length) {
      return /*#__PURE__*/React.createElement(Verdict, {
        tone: "grey",
        title: "No answers checked yet"
      }, "Click Check now to ask each assistant your questions.");
    }
    const mentioned = latest.filter(isMention);
    const byId = new Map(prompts.map(p => [String(p.id), p]));
    const parts = [];
    const byNameOnly = mentioned.length > 0 && brands.length > 0 && mentioned.every(c => this.asksByName(byId.get(String(c.promptId)), brands));
    if (byNameOnly) parts.push("Only when someone asks for you by name.");else if (mentioned.length) {
      const best = this.providers().map(p => [p, latest.filter(c => String(c.provider).toLowerCase() === p && isMention(c)).length]).sort((a, b) => b[1] - a[1])[0];
      if (best && best[1]) parts.push(`${providerLabel(best[0])} mentions you most.`);
    }
    const compCount = {};
    latest.forEach(c => parseList(c.competitorMentions).forEach(d => {
      compCount[d] = (compCount[d] || 0) + 1;
    }));
    const topComps = Object.entries(compCount).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([d]) => d);
    const cit = this.state.citations;
    const topCited = cit && cit.domains ? cit.domains.filter(d => !d.owned).slice(0, 2).map(d => d.domain) : [];
    if (topComps.length && topCited.length) parts.push(`${byNameOnly ? "For general questions they" : "They"} suggest ${joinNames(topComps)} instead, mostly citing ${joinNames(topCited)}.`);else if (topComps.length) parts.push(`${byNameOnly ? "For general questions they" : "They"} suggest ${joinNames(topComps)} instead.`);else if (topCited.length) parts.push(`The sites they cite most are ${joinNames(topCited)}.`);
    const share = mentioned.length / latest.length;
    const tone = mentioned.length === 0 ? "red" : share > 0.5 ? "green" : "amber";
    return /*#__PURE__*/React.createElement(Verdict, {
      tone: tone,
      title: `AI assistants mention you in ${mentioned.length} of ${latest.length} answers`
    }, parts.join(" "));
  }
  renderModels(prompts, cells, brands) {
    const byId = new Map(prompts.map(p => [String(p.id), p]));
    return /*#__PURE__*/React.createElement("div", {
      className: "aps-models"
    }, this.providers().map(prov => {
      const answers = [...cells.values()].filter(c => String(c.provider).toLowerCase() === prov);
      const hits = answers.filter(isMention);
      let line = "Not checked yet";
      let tone = "grey";
      if (answers.length) {
        if (!hits.length) {
          line = "Hasn't mentioned you yet";
          tone = "red";
        } else {
          tone = hits.length > answers.length / 2 ? "green" : "amber";
          if (brands.length && hits.every(c => this.asksByName(byId.get(String(c.promptId)), brands))) line = "Mentions you by name only";else if (hits.length === answers.length) line = "Mentions you every time";else line = "Mentions you for some questions";
        }
      }
      return /*#__PURE__*/React.createElement("div", {
        key: prov,
        className: "aps-card aps-model"
      }, /*#__PURE__*/React.createElement("b", null, providerLabel(prov)), /*#__PURE__*/React.createElement("span", {
        className: "aps-dim"
      }, line), /*#__PURE__*/React.createElement(Chip, {
        tone: tone
      }, answers.length ? `${hits.length} of ${answers.length}` : "No answers"));
    }));
  }
  renderAnswer(prompt, cells, providers) {
    const {
      expanded
    } = this.state;
    const avail = providers.filter(p => cells.get(`${String(prompt.id)}|${p}`));
    const cur = expanded.split("|")[1];
    const cell = cells.get(expanded);
    if (!cell) return null;
    const sources = parseList(cell.sources);
    const text = cell.mentionContext || cell.answerExcerpt;
    const status = cell.cited ? "Mentioned you and linked to your site" : cell.mentioned ? "Mentioned you" : "Didn't mention you";
    return /*#__PURE__*/React.createElement("tr", {
      className: "aps-answer-row"
    }, /*#__PURE__*/React.createElement("td", {
      colSpan: 99
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-answer"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-tabs"
    }, avail.map(p => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: p,
      className: `aps-pill${p === cur ? " on" : ""}`,
      onClick: e => {
        e.stopPropagation();
        this.setState({
          expanded: `${String(prompt.id)}|${p}`
        });
      }
    }, providerLabel(p)))), /*#__PURE__*/React.createElement("div", {
      className: "aps-answer-h"
    }, /*#__PURE__*/React.createElement(Chip, {
      tone: isMention(cell) ? "green" : "grey"
    }, status), /*#__PURE__*/React.createElement("span", {
      className: "aps-dim"
    }, providerLabel(cell.provider), cell.model ? ` (${cell.model})` : "", ", ", fmtDate(cell.checkedDate))), text ? /*#__PURE__*/React.createElement("div", {
      className: "aps-answer-text"
    }, String(text).slice(0, 600)) : null, sources.length ? /*#__PURE__*/React.createElement("div", {
      className: "aps-dim aps-sources"
    }, "Sources: ", sources.slice(0, 5).map((s, i) => {
      const url = s && typeof s === "object" ? s.url : s;
      const label = s && typeof s === "object" ? s.title || s.url : s;
      return /*#__PURE__*/React.createElement("a", {
        key: i,
        href: url,
        target: "_blank",
        rel: "noreferrer"
      }, label);
    })) : cell.mentioned ? /*#__PURE__*/React.createElement("div", {
      className: "aps-dim aps-sources"
    }, "None of the sources were your site. Clear product and service pages give assistants something of yours to link to.") : null)));
  }
  renderQuestions(prompts, cells, noSelf) {
    const {
      expanded,
      busyId
    } = this.state;
    const providers = this.providers();
    const vis = this.state.vis || [];
    const byTopic = new Map();
    prompts.forEach(p => {
      const tkey = p.topicGroup || "General";
      if (!byTopic.has(tkey)) byTopic.set(tkey, []);
      byTopic.get(tkey).push(p);
    });
    // Group lines use the same latest answer per question and assistant as
    // the headline and the You column (the stored topic rollups also count
    // deleted questions and every check in 90 days, so they disagreed).
    const groupLine = tPrompts => {
      const answers = [].concat(...tPrompts.map(p => providers.map(prov => cells.get(`${String(p.id)}|${prov}`)).filter(Boolean)));
      if (!answers.length) return null;
      const hits = answers.filter(isMention).length;
      const cited = answers.filter(c => c && c.cited).length;
      return `mentioned in ${hits} of ${answers.length} answers${cited ? `, your site linked in ${cited}` : ""}`;
    };
    const groups = [...byTopic.entries()];
    const showHeaders = groups.length > 1;
    return /*#__PURE__*/React.createElement("section", {
      className: "aps-card aps-questions"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Questions we ask"), /*#__PURE__*/React.createElement("span", null, "Click a question to read each assistant's answer.")), /*#__PURE__*/React.createElement("div", {
      className: "aps-tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "aps-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Question"), /*#__PURE__*/React.createElement("th", null, "You"), /*#__PURE__*/React.createElement("th", null, "Who they suggest instead"), /*#__PURE__*/React.createElement("th", {
      className: "act"
    }))), /*#__PURE__*/React.createElement("tbody", null, groups.map(([tkey, tPrompts]) => /*#__PURE__*/React.createElement(React.Fragment, {
      key: tkey
    }, showHeaders && (() => {
      const line = noSelf ? null : groupLine(tPrompts);
      return /*#__PURE__*/React.createElement("tr", {
        className: "aps-group"
      }, /*#__PURE__*/React.createElement("td", {
        colSpan: 4
      }, /*#__PURE__*/React.createElement("b", null, tkey), line ? /*#__PURE__*/React.createElement("span", {
        className: "aps-dim"
      }, " \xB7 ", line) : null));
    })(), tPrompts.map(p => {
      const id = String(p.id);
      const answers = providers.map(prov => cells.get(`${id}|${prov}`)).filter(Boolean);
      const hits = answers.filter(isMention).length;
      const comps = [...new Set([].concat(...answers.map(c => parseList(c.competitorMentions))))];
      const hist = vis.filter(r => String(r.promptId) === id);
      const open = expanded && expanded.startsWith(`${id}|`);
      const first = answers[0];
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: id
      }, /*#__PURE__*/React.createElement("tr", {
        className: `${first ? "clickable" : ""}${open ? " open" : ""}`,
        onClick: first ? () => this.setState({
          expanded: open ? null : `${id}|${String(first.provider).toLowerCase()}`
        }) : undefined
      }, /*#__PURE__*/React.createElement("td", {
        className: "q"
      }, p.prompt), /*#__PURE__*/React.createElement("td", {
        title: hist.length ? `Last 90 days: mentioned in ${hist.filter(isMention).length} of ${hist.length} answers` : undefined
      }, !answers.length ? /*#__PURE__*/React.createElement("span", {
        className: "aps-dim"
      }, "Not checked yet") : noSelf ? /*#__PURE__*/React.createElement("span", {
        className: "aps-dim"
      }, "Your site isn't set") : hits ? /*#__PURE__*/React.createElement(Chip, {
        tone: "green"
      }, "Mentioned in ", hits, " of ", answers.length) : /*#__PURE__*/React.createElement(Chip, {
        tone: "grey"
      }, "Not mentioned")), /*#__PURE__*/React.createElement("td", {
        className: "aps-dim"
      }, comps.join(", ")), /*#__PURE__*/React.createElement("td", {
        className: "act"
      }, /*#__PURE__*/React.createElement(RowMenu, {
        disabled: busyId === id,
        items: [{
          label: "Delete",
          confirm: "Confirm delete",
          danger: true,
          onClick: () => this.deletePrompt(p)
        }]
      }))), open ? this.renderAnswer(p, cells, providers) : null);
    })))))));
  }
  renderCitations() {
    const cit = this.state.citations;
    if (!cit || !(cit.domains || []).length) return null;
    const others = cit.domains.filter(d => !d.owned).slice(0, 10);
    const max = Math.max(1, ...others.map(d => Number(d.share) || 0));
    return /*#__PURE__*/React.createElement("section", {
      className: "aps-card aps-cites"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Sites AI cites instead of yours"), /*#__PURE__*/React.createElement("span", null, "Getting mentioned on these is the quickest way in.")), others.length === 0 ? /*#__PURE__*/React.createElement(Empty, null, "Every site the assistants linked to was yours.") : /*#__PURE__*/React.createElement("div", {
      className: "aps-tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "aps-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Site"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Times cited"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Share"))), /*#__PURE__*/React.createElement("tbody", null, others.map((d, i) => /*#__PURE__*/React.createElement("tr", {
      key: i
    }, /*#__PURE__*/React.createElement("td", null, d.domain), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, d.citations), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement("span", {
      className: "aps-share"
    }, /*#__PURE__*/React.createElement("span", {
      className: "aps-bar"
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        width: `${Math.max(3, (Number(d.share) || 0) / max * 100)}%`
      }
    })), Math.round(Number(d.share) || 0), "%"))))))), /*#__PURE__*/React.createElement("div", {
      className: "aps-dim aps-note"
    }, cit.totalCitations, " links across your answers. ", Math.round(Number(cit.ownedShare) || 0), "% point to your own site."));
  }
  renderAudit() {
    const {
      auditTerm,
      auditRan
    } = this.state;
    return /*#__PURE__*/React.createElement("section", {
      className: "aps-card aps-audit"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Answer audit"), /*#__PURE__*/React.createElement("span", null, "Check whether AI answers still repeat a name or claim, like a former employee or a closed location.")), /*#__PURE__*/React.createElement("div", {
      className: "aps-row"
    }, /*#__PURE__*/React.createElement("input", {
      className: "wide",
      value: auditTerm,
      onChange: e => this.setState({
        auditTerm: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter") this.runAudit();
      },
      placeholder: "Name or phrase"
    }), /*#__PURE__*/React.createElement(Button, {
      disabled: !auditTerm.trim(),
      onClick: this.runAudit
    }, "Search answers")), auditRan && (auditRan.hits.length === 0 ? /*#__PURE__*/React.createElement("div", {
      className: "aps-result good"
    }, "\"", auditRan.term, "\" doesn't appear in any saved answer. The assistants aren't repeating it.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "aps-result warn"
    }, "\"", auditRan.term, "\" appears in ", auditRan.hits.length, " saved answer", auditRan.hits.length === 1 ? "" : "s", ", newest first. Once your site is fixed, check again over the coming weeks and watch this drop to zero."), /*#__PURE__*/React.createElement("div", {
      className: "aps-tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "aps-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Question"), /*#__PURE__*/React.createElement("th", null, "Assistant"), /*#__PURE__*/React.createElement("th", null, "Checked"), /*#__PURE__*/React.createElement("th", null, "Where it appears"))), /*#__PURE__*/React.createElement("tbody", null, auditRan.hits.slice(0, 25).map((h, i) => {
      const text = h.mentionContext || h.answerExcerpt || "";
      const idx = text.toLowerCase().indexOf(auditRan.term.toLowerCase());
      const snip = idx >= 0 ? "…" + text.slice(Math.max(0, idx - 60), idx + auditRan.term.length + 60) + "…" : "(in the sources or suggested sites)";
      return /*#__PURE__*/React.createElement("tr", {
        key: i
      }, /*#__PURE__*/React.createElement("td", {
        className: "q"
      }, h.promptText), /*#__PURE__*/React.createElement("td", null, providerLabel(h.provider)), /*#__PURE__*/React.createElement("td", null, fmtDate(h.checkedDate)), /*#__PURE__*/React.createElement("td", {
        className: "aps-dim"
      }, snip));
    })))))));
  }
  renderAdd() {
    const {
      addText,
      saving
    } = this.state;
    const company = (account.get() || {}).company || "your company";
    const SUGGESTED = [["Your team", `Who works at ${company}?`], ["Locations and hours", `Where is ${company} located and what are its hours?`], ["What you offer", `What does ${company} offer?`]];
    return /*#__PURE__*/React.createElement("section", {
      className: "aps-card aps-add"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Add questions"), /*#__PURE__*/React.createElement("span", null, "One per line. Ask what a buyer would ask, without naming you, to see if assistants bring you up on their own.")), /*#__PURE__*/React.createElement("div", {
      className: "aps-tags"
    }, /*#__PURE__*/React.createElement("span", {
      className: "aps-dim"
    }, "Also worth checking what they say about you:"), SUGGESTED.map(([label, text]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: label,
      className: "aps-pill",
      title: text,
      onClick: () => this.setState({
        addText: (addText ? addText + "\n" : "") + text
      })
    }, label))), /*#__PURE__*/React.createElement("textarea", {
      value: addText,
      rows: 3,
      autoFocus: true,
      onChange: e => this.setState({
        addText: e.target.value
      }),
      placeholder: "Who are the best companies for [what you sell]?\nWhat should I look for when buying [what you sell]?"
    }), /*#__PURE__*/React.createElement("div", {
      className: "aps-row"
    }, /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !addText.trim() || saving,
      onClick: this.addPrompts
    }, saving ? "Adding…" : "Add"), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        adding: false,
        addText: ""
      })
    }, "Cancel")));
  }
  render() {
    const {
      prompts,
      vis,
      error,
      adding,
      checking,
      notice
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (prompts === null || vis === null) return /*#__PURE__*/React.createElement(Loader, null);
    const cells = this.latestCells();
    const latest = [...cells.values()];
    const brands = this.brandTerms();
    const hasCites = this.state.citations && (this.state.citations.domains || []).length > 0;
    // Mentions are matched against the competitor registry's "self" row;
    // without it nothing can count as a mention (competitors null = the
    // call failed, so don't ask on a guess).
    const noSelf = Array.isArray(this.state.competitors) && !selfRow(this.state.competitors);
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view aivis-view wa-view aps-root"
    }, /*#__PURE__*/React.createElement(BackToApps, {
      title: "AI Visibility",
      crumb: "AI Visibility",
      subtitle: "Whether AI assistants mention you when buyers ask questions",
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
        onClick: () => this.setState({
          adding: !adding
        })
      }, "Add questions"), /*#__PURE__*/React.createElement(Button, {
        themeColor: "primary",
        disabled: checking || !prompts.length,
        onClick: this.checkNow
      }, checking ? "Checking…" : "Check now"))
    }), notice && /*#__PURE__*/React.createElement("div", {
      className: "aps-notice"
    }, notice, " ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "aps-link",
      onClick: () => this.setState({
        notice: null
      })
    }, "Dismiss")), adding && this.renderAdd(), noSelf && prompts.length > 0 ? /*#__PURE__*/React.createElement(SelfSiteCard, {
      competitors: this.state.competitors,
      what: "Answers are checked for your business name and website. Until we know which site is yours, no answer can count as a mention.",
      onSaved: () => {
        this.reload();
        this.setState({
          notice: "Saved. Click Check now to ask the assistants again."
        });
      }
    }) : this.renderVerdict(prompts, latest, brands, cells), prompts.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, !noSelf && this.renderModels(prompts, cells, brands), /*#__PURE__*/React.createElement("div", {
      className: hasCites ? "aps-split wide" : ""
    }, /*#__PURE__*/React.createElement("div", {
      className: "aps-main"
    }, this.renderQuestions(prompts, cells, noSelf)), hasCites ? /*#__PURE__*/React.createElement("div", {
      className: "aps-side"
    }, this.renderCitations()) : null), this.renderAudit()));
  }
}
export default withRouter(AiVisibilityViewComponent);